import { getGetApiGroupQueryOptions, type GroupViewDTO } from '@smartsash/api-client';
import { defineDataScreen, pagedQuery } from '@smartsash/data-screen';
import { Avatar, Badge, Button, Icon } from '@smartsash/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ColumnDef, TableMeta } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import styles from './Groups.module.css';
import { GroupsFilter } from './GroupsFilter';
import {
  groupColumnText,
  groupsQueryParams,
  groupsSearchSchema,
  type GroupColumnId,
} from './groupsModel';
import MobileCard from './MobileCard';
import RowActions from './RowActions';

import { useDataScreenTranslate } from '@/hooks/useDataScreenTranslate';
import { useInstitutionNames, useLabCountByGroupId } from '@/hooks/useLookupNames';

/**
 * Column headers are rendered, not built: the definition lives at module scope and cannot call
 * `t`, but a component can.
 */
function ColumnHeader({ column }: { column: GroupColumnId }) {
  const { t } = useTranslation();
  return <>{t(`groups.columns.${column}`)}</>;
}

function InstitutionCell({
  row,
  meta,
}: {
  row: GroupViewDTO;
  meta: TableMeta<GroupViewDTO> | undefined;
}) {
  return (
    <Badge variant="modern" size="sm" iconLeading={<Icon name="bank" />}>
      {groupColumnText.institution(row, meta)}
    </Badge>
  );
}

function LaboratoriesCell({
  row,
  meta,
}: {
  row: GroupViewDTO;
  meta: TableMeta<GroupViewDTO> | undefined;
}) {
  return (
    <Badge variant="modern" size="sm" iconLeading={<Icon name="lab" />}>
      {groupColumnText.laboratories(row, meta)}
    </Badge>
  );
}

const columns: ColumnDef<GroupViewDTO>[] = [
  {
    id: 'name',
    header: () => <ColumnHeader column="name" />,
    cell: ({ row }) => (
      <Link
        to="/groups/$id"
        params={{ id: row.original.id ?? '' }}
        className={styles['company-cell']}
      >
        <span className={styles['company-name']}>{row.original.name}</span>
        <span className={styles['company-code']}>{row.original.accountNumber}</span>
      </Link>
    ),
    meta: { primary: true, sortable: true, value: groupColumnText.name },
  },
  {
    id: 'address',
    header: () => <ColumnHeader column="address" />,
    cell: ({ row }) => row.original.address,
    meta: { width: '200px', sortable: true, value: groupColumnText.address },
  },
  {
    id: 'institution',
    header: () => <ColumnHeader column="institution" />,
    cell: ({ row, table }) => <InstitutionCell row={row.original} meta={table.options.meta} />,
    meta: { width: '200px', value: groupColumnText.institution },
  },
  {
    // LT-05: the design carries one manager column — avatar, name, e-mail beneath — rather than
    // the three the Institutes column set contributed.
    id: 'manager',
    header: () => <ColumnHeader column="manager" />,
    cell: ({ row }) => (
      <div className={styles['contact-cell']}>
        <Avatar name={row.original.contact ?? ''} size="sm" />
        <div className={styles['contact-stack']}>
          <span className={styles['contact-name']}>{row.original.contact}</span>
          <span className={styles['contact-email']}>{row.original.email}</span>
        </div>
      </div>
    ),
    meta: { width: '240px', value: groupColumnText.manager },
  },
  {
    // Laboratories count, derived client-side by joining the lab list on groupId (same pattern as
    // the institution name join above) since `GetApiLabParams` has no group filter.
    id: 'laboratories',
    header: () => <ColumnHeader column="laboratories" />,
    cell: ({ row, table }) => <LaboratoriesCell row={row.original} meta={table.options.meta} />,
    meta: { width: '96px', value: groupColumnText.laboratories },
  },
];

function AddGroupButton({ iconOnly = false }: { iconOnly?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goToNew = () => {
    void navigate({ to: '/groups/new' });
  };

  return iconOnly ? (
    <Button
      hierarchy="primary"
      size="sm"
      iconLeading={<Icon name="plus" />}
      iconOnly
      aria-label={t('groups.addAriaLabel')}
      onClick={goToNew}
    />
  ) : (
    <Button hierarchy="primary" size="sm" iconLeading={<Icon name="plus" />} onClick={goToNew}>
      {t('groups.add')}
    </Button>
  );
}

/**
 * The Groups list screen on `@smartsash/data-screen` (HS-869 — first slice of the plan's entity
 * order, Groups → Institutes → Laboratories → Devices → Certifications → RFID tags → Users).
 *
 * List-only by design: `detail`, `form` and `permissions` stay out while admin keeps its designed
 * detail/edit pages, and per-route `requirePermission` remains unwired (lock-out risk, HS-803).
 *
 * `searchScope`/`sortScope` are the factory's `'server'` default: the filtering-foundation API gave
 * `GetApiGroupParams` `search`, `sortBy`/`sortDir` and repeatable scope filters, so `q` and `sort`
 * reach `query` instead of narrowing the page on screen. `groupsQueryParams` turns the whole search
 * into that query and is shared with `GroupsFilter`, which has to read the same cache entry.
 *
 * What the URL now owns that the page owned before: `page` (was `useListPageParam`), `q`, `sort`
 * and `filters`. The last-page correction moved into the factory.
 */
export const groupsScreen = defineDataScreen({
  entity: 'group',
  searchSchema: groupsSearchSchema,
  useTranslate: useDataScreenTranslate,
  list: {
    // The generated query options keep the generated key, which is what server-ops invalidates
    // after a create/edit/delete — a bespoke key would leave the list stale.
    query: (search) =>
      pagedQuery<GroupViewDTO>(getGetApiGroupQueryOptions(groupsQueryParams(search))),
    columns,
    getRowId: (row) => row.id ?? '',
    useTableMeta: () => ({
      institutionNames: useInstitutionNames(),
      labCountByGroupId: useLabCountByGroupId(),
    }),
    mobileCard: (row) => <MobileCard row={row} />,
    titleKey: 'groups.title',
    emptyState: {
      titleKey: 'groups.empty.title',
      descriptionKey: 'groups.empty.description',
      filteredTitleKey: 'list.emptyFiltered.title',
      filteredDescriptionKey: 'list.emptyFiltered.description',
    },
    selectable: true,
    rowActions: [{ id: 'row-actions', render: (row) => <RowActions row={row} /> }],
    headerAction: { id: 'add', permission: 'group.create', render: () => <AddGroupButton /> },
    mobileHeaderAction: {
      id: 'add-mobile',
      permission: 'group.create',
      render: () => <AddGroupButton iconOnly />,
    },
    filterSlot: (search) => <GroupsFilter search={search} />,
  },
});
